import React, { useMemo, useState, useEffect } from "react";
import { OpportunityMetrics } from "../../jobDashboard/data/api/JobDashboardClient";
import { JobOpportunity } from "../data/api/JobOpportunityClient";
import JobApplicationClient, { JobApplication } from "../../jobApplications/data/api/JobApplicationClient";
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  Target,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  PieChart as PieChartIcon,
  Award,
  Briefcase,
  ThumbsDown,
  Layers,
  ArrowUpRight
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { motion } from 'framer-motion';
import "./JobAnalyticsTab.css";

const COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ec4899', '#8b5cf6', '#10b981', '#ef4444', '#14b8a6', '#f43f5e', '#84cc16'];
const FUNNEL_COLORS = ['#4f46e5', '#0ea5e9', '#f59e0b', '#a855f7', '#10b981', '#06b6d4', '#ec4899'];
const REJECTION_PIE_COLORS = ['#00bcd4', '#ff9800', '#ff52a2', '#8c7ae6', '#10b981', '#3b82f6', '#ec4899', '#6366f1'];
const STANDARD_SOURCES = ['Referral', 'Website', 'Agency', 'Walk-in', 'LinkedIn', 'Job portal', 'Internal', 'Other'];

const formatReason = (r: string): string => {
  if (!r) return "Unspecified";
  return r
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const RADIAN = Math.PI / 180;
const renderPieCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  if (!percent) return null;
  // Guard against pre-scaled percentages vs Recharts decimal fractions (0.33 vs 33)
  const pctValue = percent > 1 ? Math.round(percent) : Math.round(percent * 100);
  if (pctValue < 5) return null;

  const radius = innerRadius + (outerRadius - innerRadius) * 0.58;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#ffffff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="13"
      fontWeight="700"
      style={{ pointerEvents: "none" }}
    >
      {`${pctValue}%`}
    </text>
  );
};

interface JobAnalyticsTabProps {
  metrics: OpportunityMetrics;
  stages?: string[];
  job?: JobOpportunity | null;
  opportunityId?: string;
}

const parseAppDate = (dateStr?: string | null): Date | null => {
  if (!dateStr) return null;
  try {
    if (dateStr.includes(",")) {
      const parts = dateStr.split(",");
      const datePart = parts[0].trim().replace(/\//g, "-");
      const timePart = parts[1] ? parts[1].trim() : "00:00:00";
      const d = new Date(`${datePart}T${timePart}`);
      if (!isNaN(d.getTime())) return d;
      const d2 = new Date(datePart);
      if (!isNaN(d2.getTime())) return d2;
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
    const dSlash = new Date(dateStr.replace(/\//g, "-"));
    if (!isNaN(dSlash.getTime())) return dSlash;
  } catch {
    return null;
  }
  return null;
};

const JobAnalyticsTab: React.FC<JobAnalyticsTabProps> = ({
  metrics,
  stages = [],
  job,
  opportunityId,
}) => {
  const [applicants, setApplicants] = useState<JobApplication[]>([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [inflowView, setInflowView] = useState<'daily' | 'cumulative'>('daily');

  // Fetch full applicants data for deep job-level insights (skills coverage, match distribution, rejections)
  useEffect(() => {
    if (!opportunityId) return;
    setLoadingApplicants(true);
    JobApplicationClient.getApplicants(opportunityId)
      .then((res) => {
        setApplicants(res?.records || []);
      })
      .catch((err) => {
        console.warn("Could not fetch applicants for advanced analytics:", err);
      })
      .finally(() => setLoadingApplicants(false));
  }, [opportunityId]);

  // Total applications from metrics (or applicants fallback)
  const totalApps = metrics.total_applications || applicants.length || 0;
  const hiredCount = metrics.hired_count || 0;
  const rejectedCount = metrics.rejected_count || 0;

  // 1. Vacancy Fulfillment
  const vacancies = job?.vacancies || 0;
  const vacancyFillRate = vacancies > 0 ? Math.min(100, Math.round((hiredCount / vacancies) * 100)) : null;

  // 2. Timeline & Time-to-Fill Speed
  const daysActive = useMemo(() => {
    if (!job?.created_at) return null;
    const createdTime = new Date(job.created_at).getTime();
    if (isNaN(createdTime)) return null;
    return Math.max(0, Math.floor((Date.now() - createdTime) / (1000 * 60 * 60 * 24)));
  }, [job?.created_at]);

  const deadlineStatus = useMemo(() => {
    if (!job?.application_deadline) return { text: "No deadline set", status: "neutral" };
    const dl = new Date(job.application_deadline).getTime();
    if (isNaN(dl)) return { text: "No deadline set", status: "neutral" };
    const diffDays = Math.ceil((dl - Date.now()) / (1000 * 60 * 60 * 24));
    if (diffDays > 0) return { text: `${diffDays} days remaining`, status: "active" };
    if (diffDays === 0) return { text: "Closes today", status: "warning" };
    return { text: "Deadline passed", status: "expired" };
  }, [job?.application_deadline]);

  // Overall Hire Conversion Rate
  const hireRate = totalApps > 0 ? ((hiredCount / totalApps) * 100).toFixed(1) : "0";

  // 3. Application Pipeline & Funnel Calculations
  const stageList = useMemo(() => {
    return stages.length > 0
      ? stages
      : ["Applied", "Shortlisted", "Interviewed", "Offered", "Accepted", "Declined", "Rejected"];
  }, [stages]);

  // "Only show the data available to us": Filter forward stages with count > 0
  const availableFunnelStages = useMemo(() => {
    const forwardStages = stageList.filter(
      (s) => !["rejected", "declined"].includes(s.toLowerCase())
    );

    const appStageMap: Record<string, number> = {};
    applicants.forEach((a) => {
      const st = a.stage || "Applied";
      appStageMap[st] = (appStageMap[st] || 0) + 1;
    });

    const items = forwardStages.map((stage) => {
      const count = metrics.stage_counts?.[stage] ?? appStageMap[stage] ?? 0;
      return { stage, count };
    });

    // Only keep stages with count > 0 (Only show data available to us)
    const filtered = items.filter((item) => item.count > 0);

    // Fallback: If no stage had positive count but totalApps > 0, include Applied
    if (filtered.length === 0 && totalApps > 0) {
      filtered.push({ stage: "Applied", count: totalApps });
    }

    return filtered;
  }, [stageList, metrics.stage_counts, applicants, totalApps]);


  // 4. Inflow Trend (Continuous timeline derived from granular applicant timestamps)
  const inflowTrendData = useMemo(() => {
    // 1. If we have applicants with valid created_at dates, build a continuous daily timeline
    if (applicants.length > 0) {
      const parsedApplicants: { date: Date; dateKey: string }[] = [];
      applicants.forEach((app) => {
        const d = parseAppDate(app.created_at);
        if (d) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          parsedApplicants.push({ date: d, dateKey: `${y}-${m}-${day}` });
        }
      });

      if (parsedApplicants.length > 0) {
        // Find earliest applicant date
        const appTimes = parsedApplicants.map((p) => p.date.getTime());
        let startTimestamp = Math.min(...appTimes);

        // Consider job creation date if earlier
        const jobCreated = parseAppDate(job?.created_at);
        if (jobCreated && jobCreated.getTime() < startTimestamp) {
          startTimestamp = jobCreated.getTime();
        }

        const startDate = new Date(startTimestamp);
        startDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Ensure we display at least 7 days up to today so the chart renders a smooth timeline
        const daysDiff = Math.max(0, Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
        if (daysDiff < 7) {
          startDate.setDate(today.getDate() - 6);
        }

        // Build continuous calendar day map
        const dailyMap: Record<string, number> = {};
        const cursor = new Date(startDate);
        while (cursor <= today) {
          const y = cursor.getFullYear();
          const m = String(cursor.getMonth() + 1).padStart(2, "0");
          const day = String(cursor.getDate()).padStart(2, "0");
          dailyMap[`${y}-${m}-${day}`] = 0;
          cursor.setDate(cursor.getDate() + 1);
        }

        // Aggregate actual applicant counts by day
        parsedApplicants.forEach((p) => {
          if (dailyMap[p.dateKey] !== undefined) {
            dailyMap[p.dateKey] += 1;
          } else {
            dailyMap[p.dateKey] = (dailyMap[p.dateKey] || 0) + 1;
          }
        });

        const entries = Object.entries(dailyMap).sort(([a], [b]) => a.localeCompare(b));
        let runningTotal = 0;
        return entries.map(([dateKey, count]) => {
          runningTotal += count;
          const [y, m, day] = dateKey.split("-").map(Number);
          const dateObj = new Date(y, m - 1, day);
          const label = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return {
            label,
            count: inflowView === "cumulative" ? runningTotal : count,
            rawCount: count,
            cumulative: runningTotal,
            date: dateKey,
          };
        });
      }
    }

    // 2. Fallback: Monthly metrics if granular applicant timestamps are not yet loaded
    const raw = metrics.monthly_applications || {};
    const entries = Object.entries(raw).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length > 1) {
      let runSum = 0;
      return entries.map(([month, count]) => {
        runSum += count;
        const [y, m] = month.split("-");
        const dateObj = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
        const label = !isNaN(dateObj.getTime())
          ? dateObj.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
          : month;
        return {
          label,
          count: inflowView === "cumulative" ? runSum : count,
          rawCount: count,
          cumulative: runSum,
        };
      });
    }

    // 3. Fallback when minimal data: create a 7-day timeline leading to totalApps
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result = [];
    let running = 0;
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const dayCount = i === 0 ? totalApps : 0;
      running += dayCount;
      result.push({
        label,
        count: inflowView === "cumulative" ? running : dayCount,
        rawCount: dayCount,
        cumulative: running,
      });
    }
    return result;
  }, [applicants, job?.created_at, metrics.monthly_applications, totalApps, inflowView]);

  const recentInflowBadge = useMemo(() => {
    if (applicants.length > 0) {
      const now = Date.now();
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
      let count = 0;
      applicants.forEach((a) => {
        const d = parseAppDate(a.created_at);
        if (d && d.getTime() >= sevenDaysAgo) {
          count++;
        }
      });
      if (count > 0) {
        return `+${count} this week`;
      }
      return `${applicants.length} active`;
    }
    return totalApps > 0 ? `${totalApps} total` : null;
  }, [applicants, totalApps]);

  // 5. Applicant Sourcing Breakdown
  const sourceData = useMemo(() => {
    const counts = metrics.source_counts || {};
    const list = STANDARD_SOURCES.map((source) => ({
      name: source,
      value: counts[source] || 0,
    })).filter((item) => item.value > 0);

    // If all zero, fallback to what applicants list has
    if (list.length === 0 && applicants.length > 0) {
      const srcMap: Record<string, number> = {};
      applicants.forEach((app) => {
        const s = app.source || "Other";
        srcMap[s] = (srcMap[s] || 0) + 1;
      });
      return Object.entries(srcMap).map(([name, value]) => ({ name, value }));
    }
    return list;
  }, [metrics.source_counts, applicants]);

  const totalSourceCandidates = sourceData.reduce((sum, item) => sum + item.value, 0);
  const topSource = sourceData.length > 0
    ? [...sourceData].sort((a, b) => b.value - a.value)[0]
    : null;

  // 6. AI Match Score Tiers & Candidate Quality
  const qualityTiers = useMemo(() => {
    const scoredApps = applicants.filter((a) => typeof a.match_score === "number");
    const count = scoredApps.length;

    if (count > 0) {
      const highFit = scoredApps.filter((a) => (a.match_score || 0) >= 80).length;
      const moderateFit = scoredApps.filter(
        (a) => (a.match_score || 0) >= 60 && (a.match_score || 0) < 80
      ).length;
      const lowFit = scoredApps.filter((a) => (a.match_score || 0) < 60).length;

      return {
        hasData: true,
        totalScored: count,
        avgScore: Math.round(
          scoredApps.reduce((acc, curr) => acc + (curr.match_score || 0), 0) / count
        ),
        tiers: [
          { label: "Strong Fit (≥80%)", count: highFit, percent: Math.round((highFit / count) * 100), color: "#10b981" },
          { label: "Moderate Fit (60-79%)", count: moderateFit, percent: Math.round((moderateFit / count) * 100), color: "#f59e0b" },
          { label: "Low Fit (<60%)", count: lowFit, percent: Math.round((lowFit / count) * 100), color: "#ef4444" },
        ],
      };
    }

    // Fallback using metrics avg_match_score if available
    if (metrics.avg_match_score !== null && metrics.avg_match_score !== undefined) {
      const rawAvg = metrics.avg_match_score > 1 ? metrics.avg_match_score : metrics.avg_match_score * 100;
      return {
        hasData: true,
        totalScored: metrics.total_scored || totalApps,
        avgScore: Math.round(rawAvg),
        tiers: [
          { label: "Qualified Talent", count: metrics.total_scored || totalApps, percent: 100, color: "#6366f1" },
        ],
      };
    }

    return { hasData: false, totalScored: 0, avgScore: null, tiers: [] };
  }, [applicants, metrics.avg_match_score, metrics.total_scored, totalApps]);

  // 7. Skills Coverage in Talent Pool (against Job's required skills)
  const skillsCoverage = useMemo(() => {
    const req = job?.skills_required || [];
    if (req.length === 0 || applicants.length === 0) return [];

    return req.map((skill) => {
      const normalizedReq = skill.toLowerCase().trim();
      const matchingCount = applicants.filter((app) => {
        const candidateSkills = app.candidate_skills || [];
        return candidateSkills.some((s) => s.toLowerCase().trim().includes(normalizedReq) || normalizedReq.includes(s.toLowerCase().trim()));
      }).length;

      const coveragePercent = Math.round((matchingCount / applicants.length) * 100);
      return {
        skill,
        count: matchingCount,
        percent: coveragePercent,
        isScarce: coveragePercent < 30,
      };
    }).sort((a, b) => b.percent - a.percent);
  }, [job?.skills_required, applicants]);

  // 8. Rejection & Disqualification Breakdown
  const rejectionAnalysis = useMemo(() => {
    const rejectedApps = applicants.filter(
      (a) => a.rejected || a.rejection_reason_type || (a.stage || "").toLowerCase() === "rejected" || (a.stage || "").toLowerCase() === "declined"
    );

    const reasonsMap: Record<string, number> = {};
    rejectedApps.forEach((a) => {
      const raw = a.rejection_reason_type || a.rejection_reason_text || "General Requirements";
      const reason = formatReason(raw);
      reasonsMap[reason] = (reasonsMap[reason] || 0) + 1;
    });

    let entries = Object.entries(reasonsMap)
      .map(([reason, count]) => ({
        name: reason,
        reason,
        value: count,
        count,
        sharePercent: rejectedApps.length > 0 ? Math.round((count / rejectedApps.length) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    if (entries.length === 0 && (rejectedCount || 0) > 0) {
      entries = [
        {
          name: "Screening Criteria",
          reason: "Screening Criteria",
          value: rejectedCount || 1,
          count: rejectedCount || 1,
          sharePercent: 100,
        },
      ];
    }

    return {
      totalRejected: Math.max(rejectedCount || 0, rejectedApps.length),
      breakdown: entries,
    };
  }, [applicants, rejectedCount]);

  const renderStatCard = (
    key: string,
    theme: 'blue' | 'green' | 'amber' | 'purple' | 'indigo',
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    badge?: React.ReactNode,
    footnote?: React.ReactNode
  ) => (
    <div key={key} className={`jo-stat-card style-horizontal theme-${theme}`}>
      <div className={`jo-stat-icon-wrapper ${theme}`}>
        {icon}
      </div>
      <div className="jo-stat-details">
        <span className="jo-stat-label">{label}</span>
        <div className="jo-stat-value-row">
          <span className="jo-stat-value">{value}</span>
          {badge}
        </div>
        {footnote}
      </div>
    </div>
  );

  return (
    <div className="jo-analytics-container">
      {/* ── Top Executive KPI Header ── */}
      <div className="jo-analytics-summary-grid">
        {/* Total Applications */}
        {renderStatCard(
          "total_apps",
          "blue",
          <Users size={18} />,
          "Total Applications",
          totalApps,
          recentInflowBadge ? (
            <span className="jo-stat-badge neutral">
              <TrendingUp size={11} />
              <span>{recentInflowBadge}</span>
            </span>
          ) : null,
          <span className="jo-stat-footnote">All recorded candidates</span>
        )}

        {/* Vacancies & Fill Target */}
        {renderStatCard(
          "vacancies",
          "green",
          <CheckCircle2 size={18} />,
          "Vacancies Filled",
          <>
            {hiredCount}
            {vacancies > 0 && <span className="jo-stat-subvalue"> / {vacancies}</span>}
          </>,
          vacancyFillRate !== null ? (
            <span className={`jo-stat-badge ${vacancyFillRate >= 100 ? "success" : "neutral"}`}>
              {vacancyFillRate}% target
            </span>
          ) : null,
          vacancies > 0 ? (
            <div className="jo-mini-progress">
              <div
                className="jo-mini-progress-fill"
                style={{ width: `${Math.min(100, vacancyFillRate || 0)}%` }}
              />
            </div>
          ) : (
            <span className="jo-stat-footnote">No target limit set</span>
          )
        )}

        {/* Time Active & Deadline */}
        {renderStatCard(
          "speed",
          "amber",
          <Clock size={18} />,
          "Hiring Speed & SLA",
          daysActive !== null ? `${daysActive}d` : "Active",
          <span className={`jo-stat-badge ${deadlineStatus.status}`}>
            <Calendar size={10} />
            <span>{deadlineStatus.text}</span>
          </span>,
          <span className="jo-stat-footnote">Days active since posted</span>
        )}

        {/* Pipeline Hire Conversion Rate */}
        {renderStatCard(
          "conversion",
          "purple",
          <Target size={18} />,
          "Hire Conversion",
          `${hireRate}%`,
          <span className="jo-stat-badge neutral">
            <ArrowUpRight size={11} />
            <span>Applicant to Hire</span>
          </span>,
          <span className="jo-stat-footnote">{hiredCount} of {totalApps} candidates hired</span>
        )}

        {/* Average Match / Talent Quality */}
        {renderStatCard(
          "fit",
          "indigo",
          <Sparkles size={18} />,
          "Talent Fit Index",
          qualityTiers.avgScore !== null ? `${qualityTiers.avgScore}%` : "Evaluating",
          <span className="jo-stat-badge success">AI Scored</span>,
          <span className="jo-stat-footnote">Avg applicant match score</span>
        )}
      </div>

      {/* ── Section 1: Application Funnel & Inflow Trend ── */}
      <div className="jo-analytics-details-grid">
        {/* Pipeline Funnel & Stage Drop-Off */}
        <div className="jo-analytics-panel">
          <div className="jo-panel-header-row">
            <div>
              <h3 className="jo-analytics-panel-title">Application Pipeline & Funnel</h3>
              <p className="jo-panel-subtitle">Candidate progression across hiring stages</p>
            </div>
          </div>

          {availableFunnelStages.length > 0 ? (
            <div className="jo-funnel-chart-container">
              {availableFunnelStages.map((st, idx) => {
                // Continuous trapezoid slope calculation
                const N = availableFunnelStages.length;
                const maxSideInset = N === 1 ? 8 : 26;
                const topInset = (idx / N) * maxSideInset;
                const botInset = ((idx + 1) / N) * maxSideInset;
                const clipPath = `polygon(${topInset}% 0%, ${100 - topInset}% 0%, ${100 - botInset}% 100%, ${botInset}% 100%)`;

                const countLabel = `${st.count} ${st.count === 1 ? "Candidate" : "Candidates"}`;
                const tierColor = FUNNEL_COLORS[idx % FUNNEL_COLORS.length];

                return (
                  <div key={st.stage} className="jo-funnel-row">
                    <div className="jo-funnel-segment-wrapper">
                      <div
                        className="jo-funnel-segment"
                        style={{
                          clipPath,
                          backgroundColor: tierColor,
                        }}
                      >
                        <div className="jo-funnel-segment-content">
                          <span className="jo-funnel-tier-name">{st.stage.toUpperCase()}</span>
                          <span className="jo-funnel-tier-dot">•</span>
                          <span className="jo-funnel-tier-metric">{countLabel}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="jo-funnel-empty">
              <p>No application pipeline data available yet</p>
            </div>
          )}
        </div>

        {/* Application Volume Over Time */}
        <div className="jo-analytics-panel">
          <div className="jo-panel-header-row">
            <div>
              <h3 className="jo-analytics-panel-title">Application Inflow</h3>
              <p className="jo-panel-subtitle">
                {inflowView === "cumulative" ? "Accumulated talent pool growth over time" : "Daily applicant volume and application momentum"}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div className="jo-inflow-toggle-group">
                <button
                  type="button"
                  className={`jo-inflow-btn ${inflowView === "daily" ? "active" : ""}`}
                  onClick={() => setInflowView("daily")}
                  title="Show daily incoming applications"
                >
                  Daily
                </button>
                <button
                  type="button"
                  className={`jo-inflow-btn ${inflowView === "cumulative" ? "active" : ""}`}
                  onClick={() => setInflowView("cumulative")}
                  title="Show total accumulated applications over time"
                >
                  Cumulative
                </button>
              </div>
              <div className="jo-pill-tag">
                <BarChart3 size={13} />
                <span>{totalApps} Total Inflow</span>
              </div>
            </div>
          </div>

          <div className="chart-wrapper-area" style={{ height: 230, marginTop: 14 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={inflowTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="inflowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(226, 232, 240, 0.6)" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="#64748b" fontSize={11} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} stroke="#64748b" fontSize={11} />
                <RechartsTooltip
                  formatter={(val: any) => [
                    `${val} applicant${val === 1 ? "" : "s"}`,
                    inflowView === "cumulative" ? "Cumulative Total" : "Daily Inflow",
                  ]}
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: "10px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    fontSize: "0.75rem",
                    padding: "8px 12px",
                  }}
                  itemStyle={{ color: "#0f172a", fontWeight: 700 }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Applications"
                  stroke="#059669"
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#inflowGrad)"
                  dot={{ r: 3, fill: "#059669", stroke: "#ffffff", strokeWidth: 1.5 }}
                  activeDot={{ r: 5, fill: "#059669" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Section 2: Talent Quality & Sourcing Channels ── */}
      <div className="jo-analytics-details-grid">
        {/* Applicant Sourcing Channels */}
        <div className="jo-analytics-panel sources-panel">
          <div className="jo-panel-header-row">
            <div>
              <h3 className="jo-analytics-panel-title">Applicant Sourcing Channels</h3>
              <p className="jo-panel-subtitle">Channel distribution & candidate origin</p>
            </div>
            {topSource && (
              <div className="jo-pill-tag">
                <Award size={13} />
                <span>Top: {topSource.name} ({topSource.value})</span>
              </div>
            )}
          </div>

          {sourceData.length > 0 ? (
            <div className="jo-sources-content-layout">
              {/* Graph on the left */}
              <div className="chart-wrapper-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={86}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        borderRadius: "8px",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                        fontSize: "0.75rem",
                        padding: "8px 12px",
                      }}
                      itemStyle={{ color: "#0f172a", fontWeight: 600 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-center">
                  <span className="donut-num">{totalSourceCandidates.toLocaleString()}</span>
                  <span className="donut-txt">CANDIDATES</span>
                </div>
              </div>

              {/* Info on the right */}
              <div className="source-legend-wrap">
                {sourceData.map((item, idx) => {
                  const share = totalSourceCandidates > 0
                    ? Math.round((item.value / totalSourceCandidates) * 100)
                    : 0;
                  return (
                    <div key={item.name} className="source-legend-item">
                      <span
                        className="legend-dot"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="legend-name">{item.name}</span>
                      <span className="legend-count">({item.value} · {share}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="jo-analytics-empty">No source data recorded yet</div>
          )}
        </div>

        {/* Candidate Match Tiers & Quality */}
        <div className="jo-analytics-panel">
          <div className="jo-panel-header-row">
            <div>
              <h3 className="jo-analytics-panel-title">Talent Fit & Quality Breakdown</h3>
              <p className="jo-panel-subtitle">AI match distribution across the applicant pool</p>
            </div>
            {qualityTiers.avgScore !== null && (
              <div className="jo-pill-tag">
                <Sparkles size={13} />
                <span>{qualityTiers.avgScore}% Avg Match</span>
              </div>
            )}
          </div>

          {qualityTiers.hasData ? (
            <div className="jo-quality-container">
              {/* Stacked Quality Bar */}
              <div className="jo-stacked-quality-bar">
                {qualityTiers.tiers.map((tier) => (
                  <div
                    key={tier.label}
                    className="jo-quality-segment"
                    style={{
                      width: `${tier.percent}%`,
                      backgroundColor: tier.color,
                    }}
                    title={`${tier.label}: ${tier.count} (${tier.percent}%)`}
                  />
                ))}
              </div>

              {/* Quality Tiers List */}
              <div className="jo-quality-tiers-list">
                {qualityTiers.tiers.map((tier) => (
                  <div key={tier.label} className="jo-quality-tier-card">
                    <div className="tier-header">
                      <span className="tier-dot" style={{ backgroundColor: tier.color }} />
                      <span className="tier-label">{tier.label}</span>
                    </div>
                    <div className="tier-stat-row">
                      <span className="tier-count">{tier.count} candidates</span>
                      <span className="tier-pct">{tier.percent}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="jo-insight-footer">
                <Sparkles size={14} className="insight-icon" />
                <span>
                  {qualityTiers.avgScore && qualityTiers.avgScore >= 75
                    ? "Strong candidate alignment. The applicant pool closely matches your required skills."
                    : "Moderate alignment. You may want to review skills weightings or source through targeted channels."}
                </span>
              </div>
            </div>
          ) : (
            <div className="jo-analytics-empty">
              {loadingApplicants ? "Evaluating applicant match scores..." : "No match scores calculated yet"}
            </div>
          )}
        </div>
      </div>

      {/* ── Section 3: Skills Demand Coverage & Disqualification Insights ── */}
      <div className="jo-analytics-details-grid">
        {/* Required Skills Coverage in Applicant Pool */}
        <div className="jo-analytics-panel">
          <div className="jo-panel-header-row">
            <div>
              <h3 className="jo-analytics-panel-title">Required Skills Coverage</h3>
              <p className="jo-panel-subtitle">Presence of required skills across applicant resumes</p>
            </div>
            <div className="jo-pill-tag">
              <Briefcase size={13} />
              <span>{skillsCoverage.length} Required Skills</span>
            </div>
          </div>

          {skillsCoverage.length > 0 ? (
            <div className="jo-skills-coverage-container">
              {skillsCoverage.map((item) => (
                <div key={item.skill} className="skill-coverage-row">
                  <div className="skill-coverage-label-row">
                    <span className="skill-name">{item.skill}</span>
                    <div className="skill-meta">
                      {item.isScarce && <span className="skill-scarce-tag">Hard to find</span>}
                      <span className="skill-count">{item.count} candidates ({item.percent}%)</span>
                    </div>
                  </div>
                  <div className="skill-coverage-track">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.percent}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                      className={`skill-coverage-fill ${item.isScarce ? "scarce" : "abundant"}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="jo-analytics-empty">
              {job?.skills_required && job.skills_required.length > 0
                ? "Skills coverage analysis will appear once applicants are added"
                : "No required skills specified on this job opportunity"}
            </div>
          )}
        </div>

        {/* Rejection & Disqualification Insights */}
        <div className="jo-analytics-panel">
          <div className="jo-panel-header-row">
            <div>
              <h3 className="jo-analytics-panel-title">Disqualification & Rejections</h3>
              <p className="jo-panel-subtitle">Primary reasons candidates did not pass</p>
            </div>
            {rejectionAnalysis.totalRejected > 0 && (
              <div className="jo-pill-tag danger">
                <UserX size={13} />
                <span>{rejectionAnalysis.totalRejected} Disqualified</span>
              </div>
            )}
          </div>

          {rejectionAnalysis.breakdown.length > 0 ? (
            <div className="jo-rejection-pie-container">
              <div className="jo-rejection-chart-wrapper">
                <ResponsiveContainer width="100%" height={210}>
                  <PieChart>
                    <Pie
                      data={rejectionAnalysis.breakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={88}
                      dataKey="value"
                      nameKey="name"
                      labelLine={false}
                      label={renderPieCustomizedLabel}
                      stroke="#ffffff"
                      strokeWidth={2}
                    >
                      {rejectionAnalysis.breakdown.map((_, index) => (
                        <Cell
                          key={`rejection-cell-${index}`}
                          fill={REJECTION_PIE_COLORS[index % REJECTION_PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(val: any, name: any) => [
                        `${val} Candidate${Number(val) === 1 ? "" : "s"} (${
                          rejectionAnalysis.totalRejected > 0
                            ? Math.round((Number(val) / rejectionAnalysis.totalRejected) * 100)
                            : 0
                        }%)`,
                        name,
                      ]}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.96)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        borderRadius: "8px",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                        fontSize: "0.75rem",
                        padding: "8px 12px",
                      }}
                      itemStyle={{ color: "#0f172a", fontWeight: 600 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Horizontal ring-marker legend matching user's design */}
              <div className="jo-rejection-legend">
                {rejectionAnalysis.breakdown.map((item, idx) => (
                  <div key={item.reason} className="jo-rejection-legend-item">
                    <span
                      className="jo-rejection-legend-ring"
                      style={{ borderColor: REJECTION_PIE_COLORS[idx % REJECTION_PIE_COLORS.length] }}
                    />
                    <span className="jo-rejection-legend-label">{item.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="jo-zero-rejections-box">
              <CheckCircle2 size={36} color="#10b981" />
              <h4>Clean Pipeline</h4>
              <p>No candidates have been disqualified or rejected for this job opportunity yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobAnalyticsTab;
